import React, { useMemo } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { Squares2X2Icon, ChatBubbleLeftRightIcon, MegaphoneIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { MainLayout } from '../layouts';
import { AdminFeatureFlags } from './AdminFeatureFlags';
import { AdminFeedbackPage } from './AdminFeedbackPage';
import { AdminAlertsPage } from './AdminAlertsPage';
import { AdminBetaAccess } from './AdminBetaAccess';
import { logger } from '../lib/logging';
import { useAuth } from '../contexts/AuthContext';

type AdminTab = 'feature-flags' | 'feedback' | 'alerts' | 'beta-access';

const TAB_CONFIG: Array<{ id: AdminTab; label: string; description: string; icon: React.FC<{ className?: string }> }> = [
  {
    id: 'feature-flags',
    label: 'Feature Flags',
    description: 'Toggle and refresh platform feature gates.',
    icon: Squares2X2Icon,
  },
  {
    id: 'feedback',
    label: 'Feedback',
    description: 'Review all feedback captured from results.',
    icon: ChatBubbleLeftRightIcon,
  },
  {
    id: 'alerts',
    label: 'Alerts',
    description: 'Manage site-wide announcement banners.',
    icon: MegaphoneIcon,
  },
  {
    id: 'beta-access',
    label: 'Beta Access',
    description: 'Manage allowlist and access requests.',
    icon: ShieldCheckIcon,
  },
];

const ADMIN_EMAIL = 'pavlo.kosmos@gmail.com';

const AdminPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const correlationId = useMemo(() => crypto.randomUUID(), []);
  const ctx = logger.withCorrelation(correlationId);

  // Check admin access
  if (!authLoading && (!user || user.email !== ADMIN_EMAIL)) {
    logger.warn('admin.unauthorized_access', undefined, {
      ...ctx,
      userEmail: user?.email || 'anonymous'
    });
    return <Navigate to="/" replace />;
  }

  // Show loading while checking auth
  if (authLoading) {
    return (
      <MainLayout showBreadcrumb={true} breadcrumbText="Admin">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </MainLayout>
    );
  }

  const requestedTab = (searchParams.get('tab') as AdminTab | null) ?? 'feature-flags';
  const activeTab = TAB_CONFIG.some((tab) => tab.id === requestedTab) ? requestedTab : 'feature-flags';

  const handleTabChange = (tab: AdminTab) => {
    setSearchParams({ tab });
    logger.info('admin.tab_change', undefined, { ...ctx, tab });
  };

  const getBreadcrumbText = () => {
    if (activeTab === 'feedback') return 'Admin / Feedback';
    if (activeTab === 'alerts') return 'Admin / Alerts';
    if (activeTab === 'beta-access') return 'Admin / Beta Access';
    return 'Admin / Feature Flags';
  };

  return (
    <MainLayout showBreadcrumb={true} breadcrumbText={getBreadcrumbText()}>
      <div className="px-4 py-6 sm:px-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex flex-col gap-2">
            <h1 className="text-2xl font-bold text-white sm:text-3xl">Admin Console</h1>
            <p className="text-slate-300">Manage feature flags, user feedback, and site announcements.</p>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            {TAB_CONFIG.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
                  activeTab === tab.id
                    ? 'bg-cyan-900/30 border-cyan-500/50 text-white'
                    : 'bg-slate-800/50 border-slate-700/60 text-slate-300 hover:border-slate-500/60'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                <div className="text-left">
                  <div className="text-sm font-semibold">{tab.label}</div>
                  <div className="text-xs text-slate-400">{tab.description}</div>
                </div>
              </button>
            ))}
          </div>

          <div className="bg-slate-900/40 border border-slate-800/60 rounded-2xl">
            {activeTab === 'feedback' ? (
              <AdminFeedbackPage embedded correlationId={correlationId} />
            ) : activeTab === 'alerts' ? (
              <AdminAlertsPage embedded correlationId={correlationId} />
            ) : activeTab === 'beta-access' ? (
              <AdminBetaAccess embedded correlationId={correlationId} />
            ) : (
              <AdminFeatureFlags embedded correlationId={correlationId} />
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AdminPage;
